/**
 * Is this document the PUBLIC customer runtime (`/ext/workflows/{id}/new`)?
 *
 * <h3>Why this exists</h3>
 * A public customer has no session, and the requirement for their runtime is that PDF
 * generation is the only request it makes. `GET /api/v1/me` breaks that, and not just once:
 * `FieldRenderer` calls `useSession()` once per FIELD, `StepContent` is keyed on the active
 * step so the whole field tree remounts on every step change, and `fetchSessionOnce` only
 * short-circuits on a NON-NULL cached session. A 401 caches null and stamps `_failedAt`, so
 * every remount more than `FAILURE_RETRY_MS` after the last attempt asks again — which makes
 * a page change fire a fresh `/me`.
 *
 * That retry policy is correct for staff, where a null may be a transport blip worth
 * retrying. It is simply wrong for a runtime that will never have a session, so the fix is to
 * not ask at all rather than to weaken the policy for everyone.
 *
 * <h3>Why the URL rather than state</h3>
 * Same reasoning as `AppShell` picking its header variant from `location.pathname`: it cannot
 * get out of step with where the user actually is, there is no ordering hazard about who sets
 * it before whom, it survives a reload, and it works from module scope — which matters,
 * because `fetchSessionOnce` is module-level and has no React context to read.
 */

/** URL prefix of the public customer runtime. Kept beside the predicate so the two agree. */
export const PUBLIC_RUNTIME_PATH_PREFIX = '/ext/';

export function isAnonymousRuntime(): boolean {
  return (
    typeof window !== 'undefined' && window.location.pathname.startsWith(PUBLIC_RUNTIME_PATH_PREFIX)
  );
}
